import API, { apiErrorMessage } from "../global/api";
import { listPaging } from "../global/Utils";

// Add Buyer
export const addBuyerRequest = () => {
  return {
    type: "ADD_BUYER_REQUEST",
  };
};
export const addBuyerSuccess = (data) => {
  return {
    type: "ADD_BUYER_SUCCESS",
    payload: data,
  };
};
export const addBuyerError = (msg) => {
  return {
    type: "ADD_BUYER_ERROR",
    payload: msg,
  };
};
// The add-buyer alert: the server's message, then what its FBR and ATL checks
// found. Only a Registered buyer is checked, so any other buyer gets the
// message alone.
export const buyerAddedText = ({ message, data } = {}) => {
  const statuses = [
    data?.salesTaxStatus && `Sales Tax Status: ${data.salesTaxStatus}`,
    data?.atlStatus && `ATL Status: ${data.atlStatus}`,
  ].filter(Boolean);
  if (!statuses.length) return message;
  const lead = message ? `${message.replace(/\.$/, "")}. ` : "";
  return `${lead}${statuses.join(", ")}.`;
};

export const addBuyer = (
  data,
  setFormData,
  handleVisible,
  handleDangerVisible,
) => {
  return (dispatch) => {
    dispatch(addBuyerRequest());
    API.post(`/buyer/add-buyer`, data)
      .then((res) => {
        let data1 = res.data;
        dispatch(addBuyerSuccess(data1));
        handleVisible(buyerAddedText(res.data));
        setFormData({
          businessName: "",
          contact: "",
          registrationNo: "",
          saletax: "",
          registrationType: "",
          province: "",
          address: "",
          userId: "",
        });
      })
      .catch((error) => {
        dispatch(addBuyerError(apiErrorMessage(error)));
        handleDangerVisible(apiErrorMessage(error));
      });
  };
};

// Live tax status on the Add Buyer form.
//
// Two independent checks, one per status box, each with its own loading,
// error and Retry, so the quick ATL answer never waits on FBR. A new number,
// or leaving the form, makes any answer still in flight stale: only the
// newest request of each check is allowed to land.
const TAX_STATUS_CHECKS = {
  salesTax: { url: "/buyer/get-sales-tax-status", field: "salesTaxStatus" },
  atl: { url: "/buyer/get-atl-status", field: "atlStatus" },
};
const latestTaxStatusRequest = { salesTax: 0, atl: 0 };

export const getBuyerTaxStatusRequest = (check) => ({
  type: "BUYER_TAX_STATUS_REQUEST",
  payload: { check },
});
// `taxpayer`: the ATL row found ({ name, businessName, businessNames, ... }),
// null when the number is not on the list. The sales tax check has none.
export const getBuyerTaxStatusSuccess = (check, status, taxpayer = null) => ({
  type: "BUYER_TAX_STATUS_SUCCESS",
  payload: { check, status, taxpayer },
});
export const getBuyerTaxStatusError = (check, msg) => ({
  type: "BUYER_TAX_STATUS_ERROR",
  payload: { check, message: msg },
});

/** One box's check; also its Retry. `check`: "salesTax" or "atl". */
export const checkBuyerTaxStatus = (check, registrationNo) => {
  return (dispatch) => {
    const { url, field } = TAX_STATUS_CHECKS[check];
    const request = ++latestTaxStatusRequest[check];
    dispatch(getBuyerTaxStatusRequest(check));
    API.get(`${url}?registrationNo=${encodeURIComponent(registrationNo)}`)
      .then((res) => {
        if (request !== latestTaxStatusRequest[check]) return;
        const status = res.data?.[field];
        if (status) {
          dispatch(
            getBuyerTaxStatusSuccess(check, status, res.data?.taxpayer ?? null),
          );
        } else {
          dispatch(
            getBuyerTaxStatusError(check, "Unexpected answer from the server."),
          );
        }
      })
      .catch((error) => {
        if (request !== latestTaxStatusRequest[check]) return;
        dispatch(
          getBuyerTaxStatusError(
            check,
            error?.response?.data?.message ||
              "Could not get the status. Please try again.",
          ),
        );
      });
  };
};

export const getBuyerTaxStatus = (registrationNo) => {
  return (dispatch) => {
    dispatch(checkBuyerTaxStatus("salesTax", registrationNo));
    dispatch(checkBuyerTaxStatus("atl", registrationNo));
  };
};

export const clearBuyerTaxStatus = () => {
  latestTaxStatusRequest.salesTax += 1;
  latestTaxStatusRequest.atl += 1;
  return { type: "BUYER_TAX_STATUS_CLEAR" };
};

// One buyer's standing on the Active Taxpayer List right now, for the invoice
// forms' Section 236H rate. Resolves to "Active" / "Inactive"; rejects when
// the list could not be checked. Nothing is kept in the store: the form that
// asked holds the answer for the buyer it has selected.
export const fetchBuyerAtlStatus = (registrationNo) => {
  return () =>
    API.get(
      `/buyer/get-atl-status?registrationNo=${encodeURIComponent(registrationNo)}`,
    ).then((res) => res.data?.atlStatus);
};

// Re-checks one saved buyer's Sales Tax and ATL status on the server - for a
// status that is out of date, or a buyer added before the checks existed -
// then re-reads the list so the new values show. A check the server could not
// complete keeps its saved value, and the message says so.
export const refreshBuyerStatusRequest = (id) => ({
  type: "REFRESH_BUYER_STATUS_REQUEST",
  payload: id,
});
export const refreshBuyerStatusSuccess = (data) => ({
  type: "REFRESH_BUYER_STATUS_SUCCESS",
  payload: data,
});
export const refreshBuyerStatusError = (msg) => ({
  type: "REFRESH_BUYER_STATUS_ERROR",
  payload: msg,
});
export const refreshBuyerStatus = (
  id,
  handleVisible,
  handleDangerVisible,
  page,
  limit,
  searchTerm,
) => {
  return (dispatch) => {
    dispatch(refreshBuyerStatusRequest(id));
    API.post(`/buyer/refresh-status/${id}`)
      .then((res) => {
        dispatch(refreshBuyerStatusSuccess(res.data));
        handleVisible(buyerAddedText(res.data));
        dispatch(getBuyer(page, limit, searchTerm));
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message || "Could not refresh the status";
        dispatch(refreshBuyerStatusError(message));
        handleDangerVisible(message);
      });
  };
};

// Getting Buyer List
export const getBuyerRequest = () => {
  return {
    type: "BUYER_LIST_REQUEST",
  };
};
export const getBuyerSuccess = (data) => {
  return {
    type: "BUYER_LIST_SUCCESS",
    payload: data,
  };
};
export const getBuyerError = (msg) => {
  return {
    type: "BUYER_LIST_ERROR",
    payload: msg,
  };
};
export const getBuyer = (page, limit, searchTerm) => {
  return (dispatch) => {
    dispatch(getBuyerRequest());
    const paging = listPaging(page, limit);
    API.get(
      `/buyer/get-buyers?page=${paging.page}&limit=${paging.limit}&search=${searchTerm}`,
    )
      .then((res) => {
        let hsCodeList = res.data;
        dispatch(getBuyerSuccess(hsCodeList));
      })
      .catch((error) => {
        dispatch(getBuyerError(apiErrorMessage(error)));
      });
  };
};

// Updating Buyer
export const updateBuyerRequest = () => {
  return {
    type: "UPDATE_BUYER_LIST_REQUEST",
  };
};
export const updateBuyerSuccess = (data) => {
  return {
    type: "UPDATE_BUYER_LIST_SUCCESS",
    payload: data,
  };
};
export const updateBuyerError = (msg) => {
  return {
    type: "UPDATE_BUYER_LIST_ERROR",
    payload: msg,
  };
};
export const updateBuyer = (
  id,
  data,
  handleVisible,
  handleDangerVisible,
  navigate,
) => {
  return (dispatch) => {
    dispatch(updateBuyerRequest());
    API.put(`/buyer/update-buyer/${id}`, data)
      .then((res) => {
        let updateBuyerList = res.data;
        dispatch(updateBuyerSuccess(updateBuyerList));
        handleVisible(res.data.message, () =>
          navigate("/main/view-buyer-list"),
        );
      })
      .catch((error) => {
        dispatch(updateBuyerError(apiErrorMessage(error)));
        handleDangerVisible(apiErrorMessage(error));
      });
  };
};

// Deleting Buyer
export const deleteBuyerRequest = () => {
  return {
    type: "DELETE_BUYER_LIST_REQUEST",
  };
};
export const deleteBuyerSuccess = (data) => {
  return {
    type: "DELETE_BUYER_LIST_SUCCESS",
    payload: data,
  };
};
export const deleteBuyerError = (msg) => {
  return {
    type: "DELETE_BUYER_LIST_ERROR",
    payload: msg,
  };
};
export const deleteBuyer = (
  id,
  handleVisible,
  handleDangerVisible,
  page,
  limit,
  searchTerm,
) => {
  return (dispatch) => {
    dispatch(deleteBuyerRequest());
    API.delete(`/buyer/delete-buyer/${id}`)
      .then((res) => {
        let deleteBuyerList = res.data;
        dispatch(deleteBuyerSuccess(deleteBuyerList));
        handleVisible(res.data.message);
        dispatch(getBuyer(page, limit, searchTerm));
      })
      .catch((error) => {
        dispatch(deleteBuyerError(apiErrorMessage(error)));
        handleDangerVisible(apiErrorMessage(error));
      });
  };
};


// Is getting BuyerDetails
export const getBuyerDetailsRequest = () => {
  return {
    type: "GET_BUYER_DETAILS_REQUEST",
  };
};
export const getBuyerDetailsSuccess = (data) => {
  return {
    type: "GET_BUYER_DETAILS_SUCCESS",
    payload: data,
  };
};
export const getBuyerDetailsError = (msg) => {
  return {
    type: "GET_BUYER_DETAILS_ERROR",
    payload: msg,
  };
};
export const getBuyerDetails = (id, handleDangerVisibleForBuyerDetails) => {
  return (dispatch) => {
    dispatch(getBuyerDetailsRequest());
    API.get(`/buyer/get-buyer-with-Id/${id}`)
      .then((res) => {
        let getBuyerViewDetails = res.data;
        dispatch(getBuyerDetailsSuccess(getBuyerViewDetails));
      })
      .catch((error) => {
        dispatch(getBuyerDetailsError(error?.response?.data?.message || ""));
        if (id) {
          handleDangerVisibleForBuyerDetails();
        }
      });
  };
};

// Buyer Ledger
//
// Every invoice billed to one buyer over an optional { startDate, endDate }
// window, oldest first, with the buyer's details alongside. `filters` may also
// narrow it by FBRInvoiceStatus and companyStatus, as the invoice list does.
const buyerLedgerQuery = (filters = {}) =>
  ["startDate", "endDate", "FBRInvoiceStatus", "companyStatus"]
    .filter((key) => filters[key])
    .map((key) => `${key}=${encodeURIComponent(filters[key])}`)
    .join("&");

const buyerLedgerErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export const getBuyerLedgerRequest = () => ({
  type: "BUYER_LEDGER_REQUEST",
});
export const getBuyerLedgerSuccess = (data) => ({
  type: "BUYER_LEDGER_SUCCESS",
  payload: data,
});
export const getBuyerLedgerError = (msg) => ({
  type: "BUYER_LEDGER_ERROR",
  payload: msg,
});
// Switching buyer or dates quickly can leave an older request still in flight;
// only the newest one is allowed to land, so the screen never shows one buyer's
// invoices under another's name.
let latestBuyerLedgerRequest = 0;

export const getBuyerLedger = (buyerId, filters) => {
  return (dispatch) => {
    const request = ++latestBuyerLedgerRequest;
    dispatch(getBuyerLedgerRequest());
    API.get(`/invoice/buyer-ledger/${buyerId}?${buyerLedgerQuery(filters)}`)
      .then((res) => {
        if (request !== latestBuyerLedgerRequest) return;
        dispatch(getBuyerLedgerSuccess(res.data));
      })
      .catch((error) => {
        if (request !== latestBuyerLedgerRequest) return;
        dispatch(
          getBuyerLedgerError(
            buyerLedgerErrorMessage(error, "Failed to load the buyer ledger"),
          ),
        );
      });
  };
};

export const clearBuyerLedger = () => {
  // Anything still in flight belongs to the selection just cleared.
  latestBuyerLedgerRequest += 1;
  return { type: "BUYER_LEDGER_CLEAR" };
};

// Printing the ledger.
export const exportBuyerLedgerRequest = () => ({
  type: "EXPORT_BUYER_LEDGER_REQUEST",
});
export const exportBuyerLedgerDone = () => ({
  type: "EXPORT_BUYER_LEDGER_DONE",
});

/**
 * Builds the PDF or the Excel of one buyer's ledger.
 *
 * Re-reads the window rather than printing what is in the store, so the rows,
 * the buyer and the totals on paper all come from one response.
 */
export const exportBuyerLedger = (
  buyerId,
  filters,
  type,
  details,
  handleVisible,
  handleDangerVisible,
) => {
  return (dispatch) => {
    dispatch(exportBuyerLedgerRequest());
    API.get(`/invoice/buyer-ledger/${buyerId}?${buyerLedgerQuery(filters)}`)
      .then(async (res) => {
        const invoices = res.data?.data || [];
        if (invoices.length === 0) {
          dispatch(exportBuyerLedgerDone());
          handleDangerVisible("No Data Found");
          return;
        }
        const meta = { ...details, buyer: res.data?.buyer || details?.buyer };
        // Loaded on demand - neither jsPDF nor ExcelJS is small, and most
        // visits to the ledger never print it.
        const printers = await import(
          "../Screens/Buyers/buyerLedgerPrintFunctions"
        );
        if (type === "PDF") {
          await printers.generateBuyerLedgerPdf(invoices, meta);
          handleVisible("PDF Created & Ready to Download Successfully");
        } else {
          await printers.generateBuyerLedgerExcel(invoices, meta);
          handleVisible("Downloaded Successfully");
        }
        dispatch(exportBuyerLedgerDone());
      })
      .catch((error) => {
        dispatch(exportBuyerLedgerDone());
        handleDangerVisible(
          buyerLedgerErrorMessage(error, "Failed to print the ledger"),
        );
      });
  };
};
